/**
 * Sticky reply footer for the email-thread slideout.
 *
 * Renders a `Reply to {firstName}` label on the left and Reply / Reply all
 * buttons on the right, pinned to the bottom of the record content column.
 * Self-gates to null for every non-thread entity type so it can be rendered
 * unconditionally in RecordSlideoutFooter without affecting other surfaces.
 *
 * Hooks run before the early-return guards (Rules of Hooks).
 */

import { CornerUpLeft } from '@untitledui/icons';
import { type ReactElement, useCallback, useMemo } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useIsHostedOnProposalView } from '@/contexts/ProposalViewHostContext';
import { Button } from '@/lib/base/buttons/button';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import type { CrmObject } from '@/types/crm';

import { useEmailReply } from '../timeline/events/useEmailReply';
import {
  emailAddressLabel,
  senderLabelForPayload,
} from '../timeline/threadMessages';
import { useThreadTimeline } from '../timeline/useThreadTimeline';

export function EmailThreadReplyFooter({
  object,
}: {
  object: CrmObject;
}): ReactElement | null {
  const isThread = object.entity_type_name === 'ahoy_email_thread';

  // Hooks must run before any early return (Rules of Hooks).
  // MAINTENANCE: Every hook added here MUST include `enabled: isThread` (or an
  // equivalent guard) to prevent fetching data for non-email-thread entity types.
  // RecordSlideoutFooter renders this footer unconditionally for all CRM objects; without
  // the guard a new hook would fire on contacts, companies, etc.
  const { messages } = useThreadTimeline({
    entityId: isThread ? object.entity_id : '',
    orgId: isThread ? object.org_id : '',
    enabled: isThread,
  });

  const latestMessage = messages[messages.length - 1];

  const { user } = useAuthInfo();

  const {
    canReply,
    hasOtherRecipients,
    handleReply,
    handleReplySender,
    isCreatingReply,
  } = useEmailReply({
    entityId: isThread ? object.entity_id : undefined,
    replyToEventId: latestMessage?.eventId,
    payload: latestMessage?.payload,
    isOutbound: latestMessage?.isOutbound ?? false,
    enabled: isThread,
  });

  // Derive the first name of the reply target from the latest message envelope.
  const firstName = useMemo((): string | undefined => {
    if (!latestMessage) {
      return undefined;
    }
    const { payload, isOutbound } = latestMessage;
    let label: string | undefined;
    if (!isOutbound) {
      // Inbound: replying to the sender.
      label = senderLabelForPayload(payload, user?.email);
    } else {
      // Outbound: replying continues the thread to the To recipients.
      const firstTo = payload?.to?.[0];
      label = firstTo ? emailAddressLabel(firstTo) : undefined;
    }
    if (!label || label === 'You' || label.includes('@')) {
      return undefined;
    }
    return label.split(/\s+/)[0];
  }, [latestMessage, user?.email]);

  const handleClickReply = useCallback(() => void handleReply(), [handleReply]);
  const handleClickReplySender = useCallback(
    () => void handleReplySender(),
    [handleReplySender]
  );

  // The footer is redundant when this record surface is hosted alongside a
  // proposal — the proposal already IS the drafted reply. Two signals, OR-ed:
  //  - Stack signal: the thread record is layered above a `proposal` entry
  //    (chat/sidebar surfaces). Kind-presence check, same reasoning as
  //    EmailTriggerCard.tsx's "View thread" highlight.
  //  - Host signal: a page whose primary content IS the proposal
  //    (ActionDetailPage) is mounted, where no `proposal` entry ever lands on
  //    the stack. This is an APP-LEVEL flag so it holds whether the record
  //    renders inline in the page's SplitView OR via the global overlay
  //    fallback on narrow/mobile viewports (outside the page's subtree).
  const slideoutStack = useSlideoutStackContextOptional();
  const isOnProposalStack = !!slideoutStack?.stack.some(
    entry => entry.kind === 'proposal'
  );
  const hostedOnProposalView = useIsHostedOnProposalView();
  const isHostedOnProposal = isOnProposalStack || hostedOnProposalView;

  // Gate rendering after all hooks have run.
  if (!isThread || !canReply || isHostedOnProposal) {
    return null;
  }

  const labelText = firstName ? `Reply to ${firstName}` : 'Reply';

  return (
    <div className='shrink-0 px-4 pb-3' data-component='reply-footer'>
      {/* A framed, brand-tinted bar that floats above the conversation instead
          of a flat full-width divider — the reply affordance reads as its own
          surface. Padding hugs the button so the bar stays slim. */}
      <div className='flex items-center justify-between gap-3 rounded-lg border border-[var(--color-utility-brand-200)] bg-[var(--color-bg-brand-primary)] py-1.5 pl-3.5 pr-1.5'>
        <span className='truncate text-sm font-medium text-[var(--color-text-brand-primary)]'>
          {labelText}
        </span>
        <div className='flex items-center gap-2 shrink-0'>
          <Button
            color='primary'
            size='xs'
            iconLeading={CornerUpLeft}
            onClick={handleClickReplySender}
            isDisabled={isCreatingReply}
          >
            Reply
          </Button>
          {hasOtherRecipients && (
            <Button
              color='primary'
              size='xs'
              iconLeading={CornerUpLeft}
              onClick={handleClickReply}
              isDisabled={isCreatingReply}
            >
              Reply all
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
